import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/components/BlogAudioPlayer.css';

const BlogAudioPlayer = ({ postId, postTitle, content }) => {
  const [loading, setLoading] = useState(true);
  const [audioData, setAudioData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [useBrowserTTS, setUseBrowserTTS] = useState(false);
  
  const audioRef = useRef(null);
  const utteranceRef = useRef(null);
  const speechSynthRef = useRef(null);

  useEffect(() => {
    checkAudioAvailability();
    
    // Initialize browser TTS as backup
    if ('speechSynthesis' in window) {
      speechSynthRef.current = window.speechSynthesis;
    }
  }, [postId]);

  const checkAudioAvailability = async () => {
    try {
      setLoading(true);
      const response = await api.getAudio(postId);
      
      if (response.success && response.data && response.data.audio_url) {
        // Google TTS audio exists - use it!
        setAudioData(response.data);
        setUseBrowserTTS(false);
      } else {
        // No Google audio - use browser TTS
        setUseBrowserTTS(true);
      }
    } catch (error) {
      // API failed - use browser TTS as fallback
      console.log('Google TTS not available, using browser TTS');
      setUseBrowserTTS(true);
    } finally {
      setLoading(false);
    }
  };

  // ===== GOOGLE TTS AUDIO PLAYER FUNCTIONS =====
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    } else if (utteranceRef.current) {
      utteranceRef.current.rate = speed;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  // ===== BROWSER TTS FUNCTIONS =====
  const getPlainText = (html) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const scripts = temp.getElementsByTagName('script');
    const styles = temp.getElementsByTagName('style');
    
    for (let i = scripts.length - 1; i >= 0; i--) {
      scripts[i].remove();
    }
    for (let i = styles.length - 1; i >= 0; i--) {
      styles[i].remove();
    }
    
    return temp.textContent || temp.innerText || '';
  };

  const startBrowserTTS = () => {
    if (!content) {
      toast.error('No content available to read');
      return;
    }

    const text = getPlainText(content);
    
    if (!text || text.trim().length === 0) {
      toast.error('No text content to read');
      return;
    }
    
    if (utteranceRef.current) {
      speechSynthRef.current.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = playbackRate;
    utterance.pitch = 1.0;
    utterance.volume = volume;
    
    const voices = speechSynthRef.current.getVoices();
    const preferredVoice = voices.find(voice => 
      voice.lang === 'en-US' && (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => voice.lang.startsWith('en'));
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    utterance.onstart = () => {
      setIsPlaying(true);
      setCurrentTime(0);
      // Estimate duration
      const wordCount = text.split(/\s+/).length;
      const estimatedDuration = (wordCount / 150) * 60; // 150 words per minute
      setDuration(estimatedDuration);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsPlaying(false);
      toast.error('Error playing audio');
    };

    utterance.onboundary = (event) => {
      const progressPercent = (event.charIndex / text.length) * 100;
      setCurrentTime((progressPercent / 100) * duration);
    };

    utteranceRef.current = utterance;
    speechSynthRef.current.speak(utterance);
  };

  const pauseBrowserTTS = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resumeBrowserTTS = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.resume();
      setIsPlaying(true);
    }
  };

  const stopBrowserTTS = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
    }
    setIsPlaying(false);
    setCurrentTime(0);
  };

  useEffect(() => {
    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  if (loading) {
    return null;
  }

  // Show audio player
  return (
    <div className="blog-audio-player player-active">
      {/* Hidden audio element for Google TTS */}
      {!useBrowserTTS && audioData && (
        <audio
          ref={audioRef}
          src={`https://supernovalab.ai${audioData.audio_url}`}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onError={(e) => {
            console.error('Audio load error:', e);
            toast.error('Failed to load audio, switching to browser voice');
            setUseBrowserTTS(true);
          }}
        />
      )}

      <div className="audio-player-header">
        <div className="audio-icon">
          <i className="bi bi-headphones"></i>
        </div>
        <div className="audio-title">
          <h4>Listen to Article</h4>
          <p className="audio-subtitle">{postTitle}</p>
        </div>
        {useBrowserTTS && (
          <span className="badge bg-info">Browser Voice</span>
        )}
      </div>

      <div className="audio-controls-main">
        {/* Skip Back - only for Google TTS */}
        {!useBrowserTTS && (
          <button className="btn-skip" onClick={() => skip(-10)} title="Rewind 10s">
            <i className="bi bi-skip-start-fill"></i>
            <span className="skip-time">10</span>
          </button>
        )}

        {/* Play/Pause */}
        <button className="btn-play-main" onClick={
          useBrowserTTS 
            ? (isPlaying ? pauseBrowserTTS : (currentTime > 0 ? resumeBrowserTTS : startBrowserTTS))
            : togglePlay
        }>
          {isPlaying ? (
            <i className="bi bi-pause-fill"></i>
          ) : (
            <i className="bi bi-play-fill"></i>
          )}
        </button>

        {/* Skip Forward - only for Google TTS */}
        {!useBrowserTTS ? (
          <button className="btn-skip" onClick={() => skip(10)} title="Forward 10s">
            <i className="bi bi-skip-end-fill"></i>
            <span className="skip-time">10</span>
          </button>
        ) : (
          <button className="btn-skip" onClick={stopBrowserTTS} title="Stop">
            <i className="bi bi-stop-fill"></i>
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="audio-progress-section">
        <span className="time-current">{formatTime(currentTime)}</span>
        <div className="progress-bar-container">
          {!useBrowserTTS && (
            <input
              type="range"
              min="0"
              max="100"
              value={progressPercentage}
              onChange={handleSeek}
              className="progress-slider"
            />
          )}
          <div className="progress-fill" style={{ width: `${progressPercentage}%` }}></div>
        </div>
        <span className="time-duration">{formatTime(duration)}</span>
      </div>

      {/* Additional Controls */}
      <div className="audio-additional-controls">
        {/* Speed Control */}
        <div className="speed-control">
          <i className="bi bi-speedometer2"></i>
          <div className="speed-buttons">
            {[0.75, 1, 1.25, 1.5, 2].map(speed => (
              <button
                key={speed}
                className={`btn-speed ${playbackRate === speed ? 'active' : ''}`}
                onClick={() => handleSpeedChange(speed)}
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>

        {/* Volume Control */}
        <div className="volume-control">
          <i className="bi bi-volume-up-fill"></i>
          <input
            type="range"
            min="0"
            max="100"
            value={volume * 100}
            onChange={handleVolumeChange}
            className="volume-slider"
          />
        </div>
      </div>
    </div>
  );
};

export default BlogAudioPlayer;